import React from 'react';
import { 
  Heart, Users, ShieldCheck, Award, Sparkles, Check, Globe, 
  ArrowUpRight, Target, Lightbulb, Compass
} from 'lucide-react';
import { ClarvosCTAButton, ClarvosPillBadge, AbilityBrandLogo } from './ClarvosBrand';

interface AboutUsPageProps {
  onRequestDemo: () => void;
  onExploreDemo: (tab: any) => void;
}

export const AboutUsPage: React.FC<AboutUsPageProps> = ({ onRequestDemo, onExploreDemo }) => {
  return (
    <div className="bg-clarvos-cream min-h-screen text-clarvos-black">
      
      {/* 1. Solar Yellow Hero with Large Border Radius */}
      <section className="bg-clarvos-yellow pt-32 pb-24 px-6 sm:px-12 rounded-b-[40px] relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <ClarvosPillBadge label="Our Mission" variant="dark" className="mb-6" />
          
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-clarvos-black leading-[1.1] mb-8">
            We believe every individual deserves limitless access to therapy, education, and human connection.
          </h1>
          
          <p className="text-lg sm:text-2xl text-clarvos-charcoal max-w-3xl mx-auto leading-relaxed font-normal mb-10">
            That's why we built AbilityOS. Specialized rehabilitation and assistive technology were scattered across disconnected apps, clunky hardware, and legacy binders — so we built a true clinical mission control to unify therapy, assessment, and execution.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <ClarvosCTAButton
              text="Request Platform Demo"
              hoverText="Talk With Our Team"
              onClick={onRequestDemo}
              variant="dark"
              size="lg"
            />
            <ClarvosCTAButton
              text="Explore 100+ Modules"
              hoverText="See Capabilities"
              onClick={() => onExploreDemo('modules')}
              variant="white"
              size="lg"
            />
          </div>
        </div>
      </section>

      {/* 2. Our Story Section */}
      <section className="py-24 px-6 sm:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          <div className="lg:col-span-5">
            <ClarvosPillBadge number="01" label="Our Story" variant="dark" className="mb-6" />
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight mb-6">
              Born from clinical necessity. Built for global empowerment.
            </h2>
            <p className="text-clarvos-gray-600 leading-relaxed text-base mb-8">
              Before founding AbilityOS, we witnessed therapists, special education teachers, and care teams struggling with the same fundamental problem: hours lost manually translating student communications, tracking clinical sessions on paper, and fighting incompatible hardware.
            </p>
            <div className="p-6 rounded-[24px] bg-white border border-clarvos-gray-300 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-clarvos-yellow flex items-center justify-center font-black text-xl">
                A
              </div>
              <div>
                <div className="font-bold text-base">Over 1,200+ Clinical Trials Validated</div>
                <div className="text-xs text-clarvos-gray-600">Across schools, neuro-rehab institutes, and assistive centers</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-white p-8 rounded-[32px] border border-clarvos-gray-300 shadow-sm hover:border-clarvos-black transition-all">
              <div className="w-12 h-12 rounded-2xl bg-clarvos-mint text-clarvos-black flex items-center justify-center mb-6">
                <Heart className="w-6 h-6 text-emerald-800" />
              </div>
              <h3 className="text-xl font-bold mb-2">Empathy by Design</h3>
              <p className="text-sm text-clarvos-gray-600 leading-relaxed">
                Co-designed alongside autistic advocates, speech pathologists, occupational therapists, and ALS pioneers to ensure universal dignity and non-invasive interfaces.
              </p>
            </div>

            <div className="bg-white p-8 rounded-[32px] border border-clarvos-gray-300 shadow-sm hover:border-clarvos-black transition-all">
              <div className="w-12 h-12 rounded-2xl bg-clarvos-violet text-clarvos-black flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-indigo-800" />
              </div>
              <h3 className="text-xl font-bold mb-2">Sub-50ms Latency</h3>
              <p className="text-sm text-clarvos-gray-600 leading-relaxed">
                AAC and neuro-dwell response loops operate with zero perceivable delay, keeping the rhythm of conversation as natural as organic speech.
              </p>
            </div>

            <div className="bg-white p-8 rounded-[32px] border border-clarvos-gray-300 shadow-sm hover:border-clarvos-black transition-all">
              <div className="w-12 h-12 rounded-2xl bg-clarvos-pink text-clarvos-black flex items-center justify-center mb-6">
                <Globe className="w-6 h-6 text-rose-800" />
              </div>
              <h3 className="text-xl font-bold mb-2">Multilingual Inclusion</h3>
              <p className="text-sm text-clarvos-gray-600 leading-relaxed">
                Native neural voice synthesis and symbol mapping across English, Hindi, Marathi, Spanish, and standard Braille Grade 1 & 2.
              </p>
            </div>

            <div className="bg-white p-8 rounded-[32px] border border-clarvos-gray-300 shadow-sm hover:border-clarvos-black transition-all">
              <div className="w-12 h-12 rounded-2xl bg-clarvos-yellow text-clarvos-black flex items-center justify-center mb-6">
                <ShieldCheck className="w-6 h-6 text-amber-900" />
              </div>
              <h3 className="text-xl font-bold mb-2">Enterprise Compliance</h3>
              <p className="text-sm text-clarvos-gray-600 leading-relaxed">
                Fully HIPAA, FERPA, and SOC2 compliant with local zero-knowledge encryption for student therapy and medical records.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* 3. Leadership & Clinical Board */}
      <section className="py-20 px-6 sm:px-12 bg-white border-t border-clarvos-gray-300">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <ClarvosPillBadge label="Leadership & Advisors" variant="dark" className="mb-4" />
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-4">
              Guided by Pioneers in Assistive Neuro-Tech
            </h2>
            <p className="text-clarvos-gray-600 text-base">
              A multidisciplinary council uniting clinical neurology, assistive hardware engineering, and inclusive pedagogy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Dr. Elena Rostova, MD',
                role: 'Chief of Clinical Neurology & AAC Research',
                bio: 'Former Director of Neuro-Rehabilitation at St. Jude; 20+ years researching eye-gaze and speech synthesis mechanics.',
                avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&h=300&fit=crop&crop=faces',
              },
              {
                name: 'Marcus Vance',
                role: 'Head of Assistive Hardware & Dwell Engineering',
                bio: 'Ex-Google Accessibility Lead; pioneer in non-invasive switch access and bio-signal adaptive interfaces.',
                avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&h=300&fit=crop&crop=faces',
              },
              {
                name: 'Priya Sundaram, Ed.D',
                role: 'Director of Special Education Alliances',
                bio: 'International inclusive education strategist and advisor to over 150 school districts nationwide.',
                avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300&h=300&fit=crop&crop=faces',
              },
            ].map((leader, i) => (
              <div key={i} className="bg-clarvos-cream p-8 rounded-[32px] border border-clarvos-gray-300 hover:shadow-lg transition-all group">
                <img
                  src={leader.avatar}
                  alt={leader.name}
                  className="w-20 h-20 rounded-full object-cover mb-6 border-2 border-clarvos-black"
                />
                <h4 className="text-xl font-extrabold mb-1">{leader.name}</h4>
                <div className="text-xs font-bold uppercase tracking-wider text-clarvos-yellow-hover bg-clarvos-black px-2.5 py-1 rounded-full inline-block mb-4 text-white">
                  {leader.role}
                </div>
                <p className="text-sm text-clarvos-gray-600 leading-relaxed">{leader.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Bottom CTA Section */}
      <section className="bg-clarvos-black text-white py-24 px-6 sm:px-12 rounded-t-[40px] mt-16 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-6">
            Join the Next Generation of Inclusive Care
          </h2>
          <p className="text-clarvos-gray-300 text-lg mb-10 max-w-2xl mx-auto">
            Experience how AbilityOS transforms schools, clinics, and families worldwide.
          </p>
          <div className="flex justify-center">
            <ClarvosCTAButton
              text="Schedule a Consultation"
              hoverText="Get in Touch"
              onClick={onRequestDemo}
              variant="yellow"
              size="lg"
            />
          </div>
        </div>
      </section>

    </div>
  );
};
